import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaTrash, FaFilePdf, FaSearch, FaCheck, FaChevronDown, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer, OfferStatus, OFFER_STATUS_OPTIONS } from '../services/OfferService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import PrimaryButton from '../../../components/PrimaryButton';

const BULK_STATUS_OPTIONS: { value: OfferStatus; label: string }[] = [
  { value: 'SENT', label: 'Mark as Sent' },
  { value: 'ACCEPTED', label: 'Mark as Accepted' },
  { value: 'REJECTED', label: 'Mark as Rejected' },
  { value: 'DRAFT', label: 'Revert to Draft' },
];

export default function OffersListTab(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Initialize filters from URL params
  const initialStatus = searchParams.get('status');
  const initialThisMonth = searchParams.get('thisMonth') === 'true';
  const initialPipeline = searchParams.get('pipeline');

  const getInitialStatuses = (): OfferStatus[] => {
    if (initialStatus && ['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'EXPIRED'].includes(initialStatus)) {
      return [initialStatus as OfferStatus];
    }
    return [];
  };

  const [statusFilters, setStatusFilters] = useState<OfferStatus[]>(getInitialStatuses());
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [pipelineFilter, setPipelineFilter] = useState<'' | 'open'>(
    initialPipeline === 'open' ? 'open' : ''
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<OfferStatus | ''>('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Sorting
  type SortField = 'offer_number' | 'title' | 'business_partner_name' | 'project_name' | 'status' | 'total_gross' | 'updated_at';
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Mark filters as initialized after first render
  useEffect(() => {
    setFiltersInitialized(true);
  }, []);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
      let data = await OfferService.list({
        search: searchTerm || undefined,
      });

      // Filter by selected statuses (multi-select)
      if (statusFilters.length > 0) {
        data = data.filter((offer) => statusFilters.includes(offer.status));
      }

      // Filter for open pipeline (Draft + Sent only)
      if (pipelineFilter === 'open') {
        data = data.filter((offer) => offer.status === 'DRAFT' || offer.status === 'SENT');
      }

      // Filter by date range
      if (dateFrom || dateTo) {
        data = data.filter((offer) => {
          const dateStr = offer.updated_at;
          if (!dateStr) return false;
          const offerDate = new Date(dateStr);
          offerDate.setHours(0, 0, 0, 0);

          if (dateFrom) {
            const from = new Date(dateFrom);
            from.setHours(0, 0, 0, 0);
            if (offerDate < from) return false;
          }
          if (dateTo) {
            const to = new Date(dateTo);
            to.setHours(23, 59, 59, 999);
            if (offerDate > to) return false;
          }
          return true;
        });
      }

      setOffers(data);
    } catch (err) {
      console.error('Failed to load offers:', err);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilters, dateFrom, dateTo, pipelineFilter]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const handleSearch = () => {
    loadOffers();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCreate = () => {
    navigate('/offers/new');
  };

  const handleView = (offer: Offer) => {
    navigate(`/offers/${offer.id}`);
  };

  const handleEdit = (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/offers/${offer.id}/edit`);
  };

  const handleDelete = async (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Delete offer "${offer.title}"?`);
    if (!confirmed) return;

    try {
      await OfferService.delete(offer.id);
      toast.success('Offer deleted');
      loadOffers();
    } catch (err: any) {
      console.error('Failed to delete offer:', err);
      const message = err?.response?.data?.detail || 'Delete failed';
      toast.error(message);
    }
  };

  const handleDownloadPdf = async (offer: Offer, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await OfferService.downloadPdf(offer.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Offer_${offer.offer_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(offers.map((o) => o.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (offerId: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, offerId]);
    } else {
      setSelectedIds(selectedIds.filter((id) => id !== offerId));
    }
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Please select at least one offer');
      return;
    }

    setBulkProcessing(true);
    try {
      const result = await OfferService.bulkChangeStatus(selectedIds, bulkStatus);

      if (result.updated_count === result.total_requested) {
        toast.success(`Status changed to "${OfferService.getStatusLabel(bulkStatus)}" for ${result.updated_count} offers`);
      } else if (result.updated_count > 0) {
        toast.warning(`Status changed for ${result.updated_count} of ${result.total_requested} offers`);
      } else {
        toast.error('Failed to change status for any offers');
      }

      setSelectedIds([]);
      setBulkStatus('');
      loadOffers();
    } catch (err: any) {
      console.error('Failed to bulk change status:', err);
      const message = err?.response?.data?.detail || 'Bulk status change failed';
      toast.error(message);
    } finally {
      setBulkProcessing(false);
    }
  };

  const isAllSelected = offers.length > 0 && selectedIds.length === offers.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < offers.length;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <FaSort size={12} style={{ opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />;
  };

  const sortedOffers = [...offers].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];

    // Handle null/undefined
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    // Handle numeric fields
    if (sortField === 'total_gross') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }

    // Handle date fields
    if (sortField === 'updated_at') {
      aVal = new Date(aVal).getTime() || 0;
      bVal = new Date(bVal).getTime() || 0;
    }

    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleStatusFilter = (status: OfferStatus) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Offers ({offers.length})
        </h3>
        <PrimaryButton onClick={handleCreate} icon={<FaPlus size={12} />} style={{ padding: '10px 20px' }}>
          Create offer
        </PrimaryButton>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Title, No..."
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <FaSearch
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
              }}
            />
          </div>
        </div>

        <div style={{ minWidth: '220px', position: 'relative' }} ref={statusDropdownRef}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Status
          </label>
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: statusFilters.length > 0 ? '1px solid #3b82f6' : '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: statusFilters.length > 0 ? '#eff6ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            <span style={{ color: statusFilters.length > 0 ? '#1d4ed8' : '#666' }}>
              {statusFilters.length === 0
                ? 'All statuses'
                : statusFilters.length === 1
                  ? OfferService.getStatusLabel(statusFilters[0])
                  : `${statusFilters.length} selected`}
            </span>
            <FaChevronDown size={12} style={{ color: '#999', transform: statusDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {statusDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #e0e3ef',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '12px',
                zIndex: 100,
                minWidth: '220px',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {OFFER_STATUS_OPTIONS.map((opt) => {
                  const isActive = statusFilters.includes(opt.value);
                  const statusColor = OfferService.getStatusColor(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleStatusFilter(opt.value)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: `1px solid ${isActive ? statusColor.text : '#e0e3ef'}`,
                        background: 'white',
                        color: isActive ? statusColor.text : '#666',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {statusFilters.length > 0 && (
                <button
                  onClick={() => setStatusFilters([])}
                  style={{
                    marginTop: '10px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    border: 'none',
                    background: 'transparent',
                    color: '#999',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>


        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              style={{
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                color: '#999',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Clear
            </button>
          )}
        </div>

        {pipelineFilter === 'open' && (
          <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#021a4a',
                padding: '10px 12px',
                background: '#e0f2fe',
                border: '1px solid #0ea5e9',
                borderRadius: '6px',
                fontWeight: 600,
              }}
            >
              Open pipeline
              <button
                onClick={() => {
                  setPipelineFilter('');
                  searchParams.delete('pipeline');
                  setSearchParams(searchParams);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: '#0369a1',
                  fontSize: '14px',
                  fontWeight: 700,
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#f0f4ff',
            borderRadius: '8px',
            border: '1px solid #dbeafe',
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>
            {selectedIds.length} selected
          </span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as OfferStatus | '')}
            disabled={bulkProcessing}
            style={{
              padding: '8px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              minWidth: '180px',
            }}
          >
            <option value="">Change status to...</option>
            {BULK_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleBulkStatusChange}
            disabled={!bulkStatus || bulkProcessing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: bulkStatus && !bulkProcessing ? '#021a4a' : '#ccc',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: bulkStatus && !bulkProcessing ? 'pointer' : 'not-allowed',
            }}
          >
            <FaCheck size={12} />
            {bulkProcessing ? 'Processing...' : 'Apply'}
          </button>
          <button
            onClick={() => {
              setSelectedIds([]);
              setBulkStatus('');
            }}
            disabled={bulkProcessing}
            style={{
              padding: '8px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              background: 'white',
              color: '#666',
              fontSize: '14px',
              cursor: bulkProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      ) : offers.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No offers found</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Create a new offer or adjust your search criteria.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isSomeSelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </th>
              <th style={sortableThStyle} onClick={() => handleSort('offer_number')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  No. {getSortIcon('offer_number')}
                </div>
              </th>
              <th style={sortableThStyle} onClick={() => handleSort('title')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Title {getSortIcon('title')}
                </div>
              </th>
              <th style={sortableThStyle} onClick={() => handleSort('business_partner_name')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Customer {getSortIcon('business_partner_name')}
                </div>
              </th>
              <th style={sortableThStyle} onClick={() => handleSort('project_name')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Project {getSortIcon('project_name')}
                </div>
              </th>
              <th style={{ ...sortableThStyle, width: '100px' }} onClick={() => handleSort('status')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Status {getSortIcon('status')}
                </div>
              </th>
              <th style={{ ...sortableThStyle, width: '120px' }} onClick={() => handleSort('total_gross')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                  Amount {getSortIcon('total_gross')}
                </div>
              </th>
              <th style={{ ...sortableThStyle, width: '120px' }} onClick={() => handleSort('updated_at')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Updated {getSortIcon('updated_at')}
                </div>
              </th>
              <th style={{ ...thStyle, width: '140px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedOffers.map((offer) => {
              const statusColor = OfferService.getStatusColor(offer.status);
              const isSelected = selectedIds.includes(offer.id);
              return (
                <tr
                  key={offer.id}
                  style={{
                    borderBottom: '1px solid #e0e3ef',
                    cursor: 'pointer',
                    background: isSelected ? '#f0f4ff' : 'transparent',
                  }}
                  onClick={() => handleView(offer)}
                >
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectOne(offer.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {offer.offer_number}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#021a4a' }}>{offer.title}</div>
                  </td>
                  <td style={tdStyle}>{offer.business_partner_name || '-'}</td>
                  <td style={tdStyle}>{offer.project_name || '-'}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {OfferService.getStatusLabel(offer.status)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {OfferService.formatCurrency(offer.total_gross)}
                  </td>
                  <td style={tdStyle}>{formatDate(offer.updated_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(offer);
                        }}
                        title="Details"
                        style={actionBtnStyle}
                      >
                        <FaEye size={14} />
                      </button>
                      <button
                        onClick={(e) => handleEdit(offer, e)}
                        title="Edit"
                        style={actionBtnStyle}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDownloadPdf(offer, e)}
                        title="PDF"
                        style={actionBtnStyle}
                      >
                        <FaFilePdf size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(offer, e)}
                        title="Delete"
                        style={{ ...actionBtnStyle, color: '#dc2626' }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const sortableThStyle: React.CSSProperties = {
  ...thStyle,
  cursor: 'pointer',
  userSelect: 'none',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
